import { MapBrowserEvent } from 'ol';
import React, { useContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import MapContext from 'components/MapComponent/MapContext';
import {
  defaultRrojectionExtent,
  scale_1_50_vector_clusterZoom,
  searchWktByCoords,
} from 'components/MapComponent/constants';
import WMTSSource from 'ol/source/WMTS';
import TilegridWMTS from 'ol/tilegrid/WMTS';
import { getTopLeft, getWidth } from 'ol/extent';
import TileState from 'ol/TileState';
import TileLayer from 'ol/layer/Tile';
import {
  resetAteListAction,
  resetFoundLayerAction,
  setCurrentAteAction,
  setFoundLayerAction,
} from 'store/actions/layers';
import { setBackdropLoadingAction } from 'store/actions/mainPage';
import { getCookie } from 'utils/cookie';
import { getLocalAccessToken } from 'utils/localStorage';
import { get as getProjection, transform } from 'ol/proj';

const projection = getProjection('EPSG:3857');
const projectionExtent = projection?.getExtent();
const size = getWidth(projectionExtent!) / 256;
const resolutions = new Array(19);
const matrixIds = new Array(19);
for (let z = 0; z < 19; ++z) {
  resolutions[z] = size / Math.pow(2, z);
  matrixIds[z] = z;
}

export const DrawWMTS = () => {
  const {
    currentLayer,
    currentUrl,
    currentLayerId,
    currAteVC,
    currAteCity,
    currAteRegion,
    layer_scale_1_50_vector,
  } = useAppSelector((state) => state.layers);
  const { ruler } = useAppSelector((state) => state.measurements.mode);

  const { lang, isLogged } = useAppSelector((state) => state.mainPage);
  const { map } = useContext(MapContext);
  const dispatch = useAppDispatch();
  const token = getLocalAccessToken();
  const lastSoato =
    currAteVC?.soato || currAteCity?.soato || currAteRegion?.soato;

  const findLayer = (event: MapBrowserEvent<UIEvent>) => {
    // disable searching if exist layer_scale_1_50_vector
    if (currentLayer && !ruler && !layer_scale_1_50_vector) {
      const [lon, lat] = transform(event.coordinate, 'EPSG:3857', 'EPSG:4326');
      const coords = [lon.toFixed(6), lat.toFixed(6)];

      const zoom = map.getView().getZoom();
      const zlevel = zoom ? String(zoom) : '7';

      dispatch(setBackdropLoadingAction(true));
      searchWktByCoords(String(currentLayerId), coords, zlevel, lang, isLogged)
        .then((res) => {
          const wkt = res.wkt;
          if (!wkt) return;
          const info = {
            param: res.key,
            value: res.value,
          };
          if (lastSoato !== res.soato && lastSoato && res.soato) {
            dispatch(setCurrentAteAction('currAteRegion', null));
            dispatch(resetAteListAction('ateCities', 'currAteCity'));
            dispatch(resetAteListAction('ateVC', 'currAteVC'));
          }

          dispatch(
            setFoundLayerAction(
              wkt,
              res.value,
              event.coordinate,
              res.soato,
              +zlevel
            )
          );
        })
        .catch((e) => {
          dispatch(resetFoundLayerAction());
        })
        .finally(() => {
          dispatch(setBackdropLoadingAction(false));
        });
    }
  };

  useEffect(() => {
    if (!map || !currentLayer) return;

    //add wmts layer
    let wmtsSource = new WMTSSource({
      url: currentUrl,
      crossOrigin: 'anonymous',
      layer: currentLayer,
      matrixSet: 'mymercator',
      format: 'image/png',
      // @ts-ignore
      projection: projection,
      tileGrid: new TilegridWMTS({
        origin: getTopLeft(projectionExtent || defaultRrojectionExtent),
        resolutions: [
          156543.03392804097, 78271.51696402048, 39135.75848201024,
          19567.87924100512, 9783.93962050256, 4891.96981025128,
          2445.98490512564, 1222.99245256282, 611.49622628141, 305.748113140705,
          152.8740565703525, 76.43702828517625, 38.21851414258813,
          19.109257071294063, 9.554628535647032, 4.777314267823516,
          2.388657133911758, 1.194328566955879,
        ],
        matrixIds: matrixIds,
      }),
      style: 'default',
      wrapX: true,
    });

    if (token && getCookie('user')) {
      wmtsSource.setTileLoadFunction(function (tile: any, src) {
        const xhr = new XMLHttpRequest();
        xhr.onloadstart = function () {
          xhr.responseType = 'blob';
        };
        xhr.addEventListener('loadend', function (evt) {
          const data = this.response;
          if (data) {
            tile.getImage().src = URL.createObjectURL(data);
            tile.getImage().onload = function () {
              URL.revokeObjectURL(this.src);
            };
          } else {
            tile.setState(TileState.ERROR);
          }
        });
        xhr.addEventListener('error', function () {
          tile.setState(TileState.ERROR);
        });
        xhr.open('GET', src);
        xhr.setRequestHeader(
          'Access-Control-Allow-Headers',
          'Access-Control-Allow-Headers, Origin,Accept, X-Requested-With, Content-Type, Access-Control-Request-Method, Access-Control-Request-Headers'
        );
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        xhr.send();
      });
    }

    let wmts_layer = new TileLayer({
      opacity: 1,
      source: wmtsSource,
      visible: true,
      // @ts-ignore
      name: currentLayer,
    });

    if (layer_scale_1_50_vector) {
      wmts_layer.setMaxZoom(scale_1_50_vector_clusterZoom);
    }

    map.addLayer(wmts_layer);
    map.on('click', findLayer);

    return () => {
      map.removeLayer(wmts_layer);
      map.un('click', findLayer);
    };
  }, [currentLayer]);

  useEffect(() => {
    if (!map || !currentLayer) return;

    map.on('click', findLayer);

    return () => {
      map.un('click', findLayer);
    };
  }, [map, findLayer, currentLayer, lang]);

  return null;
};
